import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

/** Synthetic first row; not stored in forum_posts. */
const FORUM_WELCOME_POST_ID = '__forum_welcome__';

const FORUM_DRAFT_DEFAULT_META = {
  category: 'keskustelu',
  eventType: 'musiikki',
  noticeType: 'myydaan',
};

function buildForumPostMeta(draft) {
  if (!draft) return 'keskustelu';
  if (draft.category === 'keskustelu') return 'keskustelu';
  if (draft.category === 'tapahtuma') {
    const sub = draft.eventType || 'musiikki';
    return `tapahtuma-${sub}`;
  }
  if (draft.category === 'ilmoitus') {
    const sub = draft.noticeType || 'myydaan';
    return `ilmoitus-${sub}`;
  }
  return 'keskustelu';
}

function postMetaToDraftExtras(postMeta) {
  const m = postMeta && String(postMeta).trim() ? String(postMeta).trim() : 'keskustelu';
  if (m === 'keskustelu') {
    return { category: 'keskustelu', eventType: 'musiikki', noticeType: 'myydaan' };
  }
  if (m.startsWith('tapahtuma-')) {
    const sub = m.slice('tapahtuma-'.length) || 'musiikki';
    const allowed = ['musiikki', 'myynti', 'kokoontuminen'];
    return {
      category: 'tapahtuma',
      eventType: allowed.includes(sub) ? sub : 'musiikki',
      noticeType: 'myydaan',
    };
  }
  if (m.startsWith('ilmoitus-')) {
    const sub = m.slice('ilmoitus-'.length) || 'myydaan';
    const allowed = ['myydaan', 'annetaan', 'ostetaan'];
    return {
      category: 'ilmoitus',
      eventType: 'musiikki',
      noticeType: allowed.includes(sub) ? sub : 'myydaan',
    };
  }
  return { ...FORUM_DRAFT_DEFAULT_META };
}

const FORUM_WELCOME_POST = {
  id: FORUM_WELCOME_POST_ID,
  title: 'Tervetuloa ilmoitustaululle',
  body: 'Täällä julkaistaan jäsenten ilmoituksia.',
  postedAt: null,
  pvmLabel: '-',
  author: 'Ylläpito',
  createdByUserId: null,
};

/** DD.mm.YYYY from ISO date/datetime (calendar date in post's Y-M-D). */
function formatPostDateFull(isoDate) {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}/.test(isoDate)) return '—';
  const datePart = isoDate.slice(0, 10);
  const [y, m, d] = datePart.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return '—';
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
}

function fiCount(n, singular, plural) {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Calendar-style elapsed parts from `from` to `to` (to >= from).
 * Omits zero components when building the label (handled by caller).
 */
function calendarElapsedParts(from, to) {
  let y = to.getFullYear() - from.getFullYear();
  let mo = to.getMonth() - from.getMonth();
  let d = to.getDate() - from.getDate();
  let h = to.getHours() - from.getHours();
  let min = to.getMinutes() - from.getMinutes();

  if (min < 0) {
    min += 60;
    h -= 1;
  }
  if (h < 0) {
    h += 24;
    d -= 1;
  }
  if (d < 0) {
    const daysInPrevMonth = new Date(to.getFullYear(), to.getMonth(), 0).getDate();
    d += daysInPrevMonth;
    mo -= 1;
  }
  if (mo < 0) {
    mo += 12;
    y -= 1;
  }

  return { years: y, months: mo, days: d, hours: h, minutes: min };
}

/** Relative "ago" string: vuodet, kuukaudet, päivät, tunnit (ei minuutteja). */
function formatForumRelativeAgo(isoDate, now = new Date()) {
  if (!isoDate) return null;
  const from = new Date(isoDate);
  if (Number.isNaN(from.getTime())) return null;
  const diffMs = now.getTime() - from.getTime();
  if (diffMs < 0) return null;
  if (diffMs < 45 * 1000) return 'juuri nyt';

  const parts = calendarElapsedParts(from, now);
  const segments = [];
  if (parts.years > 0) segments.push(fiCount(parts.years, 'vuosi', 'vuotta'));
  if (parts.months > 0) segments.push(fiCount(parts.months, 'kuukausi', 'kuukautta'));
  if (parts.days > 0) segments.push(fiCount(parts.days, 'päivä', 'päivää'));
  if (parts.hours > 0) segments.push(fiCount(parts.hours, 'tunti', 'tuntia'));

  if (segments.length === 0) return 'juuri nyt';
  return `${segments.join(' ')} sitten`;
}

function ForumPostsTable({ posts, user, searchQuery, onOpenPost, AddButton, onEditPost, onDeletePost }) {
  const q = (searchQuery || '').trim().toLowerCase();
  const welcomePost = posts.find((p) => p.id === FORUM_WELCOME_POST_ID);
  const rest = posts.filter((p) => p.id !== FORUM_WELCOME_POST_ID);
  const filteredRest = useMemo(() => {
    if (!q) return rest;
    return rest.filter(
      (p) =>
        (p.postHeader && p.postHeader.toLowerCase().includes(q)) ||
        (p.title && p.title.toLowerCase().includes(q)) ||
        (p.body && p.body.toLowerCase().includes(q)) ||
        (p.author && p.author.toLowerCase().includes(q))
    );
  }, [rest, q]);
  const filtered = welcomePost ? [welcomePost, ...filteredRest] : filteredRest;

  return (
    <section className="Dashboard-events">
      {AddButton ? <AddButton /> : null}
      <div className="Dashboard-events-header">
        <h3>Ilmoitustaulun viestit</h3>
        <hr />
      </div>

      <div className="Dashboard-events-table-wrap">
        <table className="Dashboard-events-table Forum-posts-table">
          <thead>
            <tr>
              <th>Viesti</th>
              <th>Pvm</th>
              <th className="Dashboard-events-actions-col Forum-posts-actions-head" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {filtered.map((post) => {
              const isOwn =
                user &&
                ((post.createdByUserId != null && String(post.createdByUserId) === String(user.id)) ||
                  post.author === (user.displayName || user.username));
              const fullDate = post.pvmLabel != null ? post.pvmLabel : formatPostDateFull(post.postedAt || '');
              const relative = post.pvmLabel != null ? null : formatForumRelativeAgo(post.postedAt || '');
              return (
                <tr
                  key={post.id}
                  className={`Dashboard-events-row-clickable${isOwn ? ' Dashboard-events-row--own' : ''}`}
                >
                  <td>
                    {post.id === FORUM_WELCOME_POST_ID ? (
                      <button type="button" className="EventView-open-btn" onClick={() => onOpenPost(post)}>
                        {post.title || '—'}
                      </button>
                    ) : (
                      <span className="Forum-post-title-with-badge">
                        <a className="EventView-open-btn" href={`/forum/post/${encodeURIComponent(post.id)}`}>
                          {post.title || '—'}
                        </a>
                        {isOwn && (post.unreadRepliesCount ?? 0) > 0 ? (
                          <span
                            className="Forum-post-unread-badge"
                            aria-label={`${post.unreadRepliesCount} lukematonta vastausta`}
                          >
                            {post.unreadRepliesCount > 99 ? '99+' : post.unreadRepliesCount}
                          </span>
                        ) : null}
                      </span>
                    )}
                    {!isOwn && (
                      <>
                        <br />
                        <i>Kirjoittaja</i> <strong>{post.author || '—'}</strong>
                      </>
                    )}
                  </td>
                  <td>
                    {fullDate}
                    {relative ? (
                      <span className="Forum-post-pvm-relative">
                        {' '}
                        ({relative})
                      </span>
                    ) : null}
                  </td>
                  <td className="Dashboard-events-actions-col Forum-posts-actions-cell">
                    {isOwn && (
                      <>
                        <button
                          type="button"
                          className="Event-edit"
                          title="Muokkaa"
                          aria-label="Muokkaa viestiä"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditPost(post);
                          }}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="Event-remove"
                          title="Poista"
                          aria-label="Poista viesti"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeletePost(post);
                          }}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function ForumPageView({ user, onBackToDashboard }) {
  const isPrivileged = !!user && user.role && user.role !== 'user';

  const [searchQuery, setSearchQuery] = useState('');
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState(null);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState(null);
  const [aiSearchPayload, setAiSearchPayload] = useState(null);

  useEffect(() => {
    if (!welcomeModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setWelcomeModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [welcomeModalOpen]);

  useEffect(() => {
    let cancelled = false;
    const uid =
      user?.id && isPrivileged ? String(user.id).replace(/n$/, '') : '';
    const url = uid
      ? `${API}/api/forum-posts?userId=${encodeURIComponent(uid)}`
      : `${API}/api/forum-posts`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('fetch'))))
      .then((data) => {
        if (cancelled || !data || !Array.isArray(data.posts)) return;
        setPosts(data.posts);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user, isPrivileged]);

  const displayPosts = useMemo(() => {
    const sortedDb = [...posts].sort((a, b) => {
      const ta = new Date(a.postedAt || 0).getTime();
      const tb = new Date(b.postedAt || 0).getTime();
      return tb - ta;
    });
    return [FORUM_WELCOME_POST, ...sortedDb];
  }, [posts]);

  const openCreate = () => {
    setDraft({ id: null, title: '', body: '', ...FORUM_DRAFT_DEFAULT_META });
  };

  const openEdit = (post) => {
    if (post.id === FORUM_WELCOME_POST_ID) return;
    setDraft({
      id: post.id,
      title: post.title || '',
      body: post.body || '',
      ...postMetaToDraftExtras(post.postMeta),
    });
  };

  const saveDraft = () => {
    if (!draft || !user || !isPrivileged) return;
    const title = (draft.title || '').trim();
    const body = (draft.body || '').trim();
    if (!title) return;
    const postMeta = buildForumPostMeta(draft);
    const uid = user.id;
    if (draft.id) {
      fetch(`${API}/api/forum-posts/${encodeURIComponent(draft.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: uid, title, body, postMeta }),
      })
        .then((res) => {
          if (!res.ok) return res.json().then((err) => Promise.reject(new Error(err.error || 'Virhe')));
          return res.json();
        })
        .then(({ post: saved }) => {
          setPosts((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
          setDraft(null);
        })
        .catch((e) => window.alert(e.message || 'Tallennus epäonnistui'));
    } else {
      fetch(`${API}/api/forum-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: uid, title, body, postMeta }),
      })
        .then((res) => {
          if (!res.ok) return res.json().then((err) => Promise.reject(new Error(err.error || 'Virhe')));
          return res.json();
        })
        .then(({ post: created }) => {
          setPosts((prev) => [created, ...prev]);
          setDraft(null);
        })
        .catch((e) => window.alert(e.message || 'Tallennus epäonnistui'));
    }
  };

  const deletePost = (post) => {
    if (post.id === FORUM_WELCOME_POST_ID) return;
    if (!user || !isPrivileged) return;
    if (!window.confirm('Poistetaanko viesti?')) return;
    fetch(
      `${API}/api/forum-posts/${encodeURIComponent(post.id)}?userId=${encodeURIComponent(String(user.id))}`,
      { method: 'DELETE' }
    )
      .then((res) => {
        if (res.status === 204) return;
        if (!res.ok) return res.json().then((err) => Promise.reject(new Error(err.error || 'Virhe')));
      })
      .then(() => {
        setPosts((prev) => prev.filter((p) => p.id !== post.id));
      })
      .catch((e) => window.alert(e.message || 'Poisto epäonnistui'));
  };

  const PostAddButton = () => {
    if (!user || !isPrivileged) return null;
    return (
      <button type="button" className="Event-add" onClick={openCreate} title="Luo uusi ilmoitus">
        +
      </button>
    );
  };

  const runAiForumSearch = () => {
    const q = (searchQuery || '').trim();
    if (!q) return;
    setAiSearchLoading(true);
    setAiSearchError(null);
    setAiSearchPayload(null);
    fetch(`${API}/api/forum/ai-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: q }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = data.error || data.message || `Virhe ${res.status}`;
          throw new Error(msg);
        }
        return data;
      })
      .then((data) => setAiSearchPayload(data))
      .catch((e) => setAiSearchError(e.message || 'Haku epäonnistui'))
      .finally(() => setAiSearchLoading(false));
  };

  return (
    <div className="App-block">
      <div className="Advert-wrapper Forum-search-wrapper">
        <div className="Forum-search-module" role="search">
          <label className="Forum-search-label" htmlFor="forum-search-input">
            Kerro Pulinabotille, mitä etsit..
          </label>
          <input
            id="forum-search-input"
            type="search"
            className="Forum-search-input"
            placeholder="Etsin…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                runAiForumSearch();
              }
            }}
            autoComplete="off"
          />
          <div className="Forum-ai-search-actions">
            <button
              type="button"
              className="Button Forum-ai-search-btn"
              disabled={aiSearchLoading || !(searchQuery || '').trim()}
              onClick={runAiForumSearch}
            >
              {aiSearchLoading ? 'Haetaan…' : 'Tekoälyhaku'}
            </button>
          </div>
          {aiSearchError ? <p className="Forum-ai-search-error">{aiSearchError}</p> : null}
          {aiSearchPayload?.needsIndexing && aiSearchPayload?.message ? (
            <p className="Forum-ai-search-hint">{aiSearchPayload.message}</p>
          ) : null}
          {aiSearchPayload?.intent && (aiSearchPayload.results || []).length > 0 ? (
            <div className="Forum-ai-search-results" aria-live="polite">
              <p className="Forum-ai-search-intent">
                Tulkinta: <strong>{aiSearchPayload.intent.labelFi}</strong>
              </p>
              <ol className="Forum-ai-search-result-list">
                {aiSearchPayload.results.map((r) => (
                  <li key={r.id}>
                    <a
                      className="Forum-ai-search-result-link"
                      href={r.permalink || r.permalinkPath || `/forum/post/${encodeURIComponent(r.id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {r.title || '—'}
                    </a>
                    <span className="Forum-ai-search-replies"> [{r.repliesCount ?? 0}]</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          {aiSearchPayload &&
          !aiSearchPayload.needsIndexing &&
          (aiSearchPayload.results || []).length === 0 &&
          !aiSearchLoading &&
          !aiSearchError ? (
            <p className="Forum-ai-search-empty">Ei osumia indeksoiduista viesteistä.</p>
          ) : null}
        </div>
      </div>
      <div className="Dashboard-actions">
        <div className="buttons ForumPage-toolbar-buttons">
          <button
            type="button"
            className="Button Dashboard-chat-btn ForumPage-back-btn"
            title="Takaisin etusivulle"
            aria-label="Takaisin etusivulle"
            onClick={onBackToDashboard}
          >
            <ArrowLeft size={22} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <section className="Dashboard-events">
          <ForumPostsTable
            posts={displayPosts}
            user={user}
            searchQuery={searchQuery}
            onOpenPost={(post) => {
              if (post.id === FORUM_WELCOME_POST_ID) setWelcomeModalOpen(true);
            }}
            AddButton={PostAddButton}
            onEditPost={openEdit}
            onDeletePost={deletePost}
          />
        </section>
      </div>

      {!isPrivileged ? (
        <p className="Dashboard-note">Chat ja ilmoitustaulu aktivoituvat, kun sinusta tulee jäsen.</p>
      ) : null}

      {welcomeModalOpen ? (
        <div
          className="Forum-draft-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="forum-welcome-title"
        >
          <div className="Forum-draft-panel Forum-welcome-modal-panel">
            <h4 id="forum-welcome-title">Tervetuloa ilmoitustaululle!</h4>
            <div className="Forum-welcome-modal-body">
              <p>
                Tämä on päätön ilmoitustaulu tarkoittaen, että tässä &quot;foorumissa&quot; ei ole ryhmiä. Tänne
                kirjoitetaan yksinkertaisia viestejä.
              </p>
              <p>
                Ilmoitustaululta löytää helposti etsimänsä, kun vain kysyy (joko täällä tai kirjoittamalla
                pulinabotille chätissä)
              </p>
            </div>
            <div className="Forum-draft-actions">
              <button type="button" className="Button" onClick={() => setWelcomeModalOpen(false)}>
                Sulje
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {draft ? (
        <div
          className="Forum-draft-overlay Forum-draft-overlay--compose"
          role="dialog"
          aria-modal="true"
          aria-labelledby="forum-draft-title"
        >
          <div className="Forum-draft-panel Forum-draft-panel--compose">
            <h4 id="forum-draft-title">{draft.id ? 'Muokkaa viestiä' : 'Uusi viesti'}</h4>
            <label className="Forum-draft-field">
              Otsikko
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                maxLength={200}
              />
            </label>
            <label className="Forum-draft-field Forum-draft-field--compose-body">
              Teksti
              <textarea
                value={draft.body}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              />
            </label>
            <div className="Forum-draft-meta-fields">
              <label className="Forum-draft-field">
                Viestin tyyppi
                <select
                  value={draft.category}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      category: e.target.value,
                    }))
                  }
                >
                  <option value="keskustelu">Keskustelu</option>
                  <option value="tapahtuma">Tapahtuma</option>
                  <option value="ilmoitus">Ilmoitus</option>
                </select>
              </label>
              {draft.category === 'tapahtuma' ? (
                <label className="Forum-draft-field">
                  Tapahtuman tyyppi
                  <select
                    value={draft.eventType}
                    onChange={(e) => setDraft((d) => ({ ...d, eventType: e.target.value }))}
                  >
                    <option value="musiikki">musiikki</option>
                    <option value="myynti">myynti</option>
                    <option value="kokoontuminen">kokoontuminen</option>
                  </select>
                </label>
              ) : null}
              {draft.category === 'ilmoitus' ? (
                <label className="Forum-draft-field">
                  Ilmoituksen tyyppi
                  <select
                    value={draft.noticeType}
                    onChange={(e) => setDraft((d) => ({ ...d, noticeType: e.target.value }))}
                  >
                    <option value="myydaan">Myydään</option>
                    <option value="annetaan">Annetaan</option>
                    <option value="ostetaan">Ostetaan</option>
                  </select>
                </label>
              ) : null}
            </div>
            <div className="Forum-draft-actions">
              <button type="button" className="Button Button-danger" onClick={() => setDraft(null)}>
                Peruuta
              </button>
              <button type="button" className="Button" onClick={saveDraft}>
                Tallenna
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
